import { postseasonGame } from './postseason-search.mjs';
import { leaguePicker, pickF1, pickFights, pickNascar, pickSixNations, pickTennis } from './comp-hubs.mjs';
/**
 * Tournament registry (PLAN W2-1, minimal first version): the tournaments that get a page of their own at
 * /tournament/<id>, the facts about each that a person typed in with a source, and the parsers that read a
 * partner's session name. Pure: used by scripts/gen-tournaments.mjs (build) and its test; nothing here reads
 * a file or the network.
 *
 *   kind 'sessions'  one venue, fixed sessions (afternoon / evening) with the round in the partner's name
 *   kind 'knockout'  games of a series; the build reads them from the series record (world-series), or from a
 *                    hand-entered plan of games (cfp-2026-27, cfpPlan below)
 *   kind 'roadshow'  one night per city on a fixed calendar (pl-darts-2027, plNights below)
 *   kind 'oneoff'    one marquee event (fury-joshua, fightPlan below): a single item, "Card may change"
 *   plan 'nfl'       knockout of 13 slots (road-to-sb-lxi, nflPlan below); slots take games as partners list them
 *   kind 'league'    a season competition: many clubs, home and away, rounds or matchdays (the football block
 *                    at the end of the registry; the US leagues reuse it). DATA CONTRACT, in full:
 *                      entry.league = {
 *                        clubs?       'Display name|alias|alias' strings. With clubs, a game belongs when BOTH sides are in
 *                                     the list (names compared through clubId) and its country is `country`; without
 *                                     clubs, a game belongs when its tournament text matches `memberTest` (cups,
 *                                     UEFA) and the clubs shown are the ones the games name
 *                        country?     ISO code of the venue's country ('GB'); null = any
 *                        kinds?       doc.kind values a game may carry (['soccer', 'mls']); null = any
 *                        tournamentOk? a game that names a tournament belongs only if the text matches (domestic
 *                                     leagues: a cup tie is no league game); a game with none always passes
 *                        notGame?     RegExp on the name: friendlies and exhibitions
 *                        rounds?      [{ n, from, until }] hand-entered matchday windows, with the source in facts;
 *                                     without them a round is a run of game dates less than 3 days apart
 *                        floatDates?  true: the league sets dates and times about a month ahead (said in the FAQ). A game
 *                                     the partners list on different days carries "Date not final" in any league
 *                        min?         fewest priced games for a page (default 12)
 *                        entry?       one calm sentence about personal / ID-bound tickets
 *                      }
 *                    The rows it reads (scripts/.tour-rows.json, written by scripts/gen-longtail.mjs after the legal
 *                    rules ran): { id, name, tournament, teams: [home, away], date, time, venue, city, country, kind,
 *                    demand, held, path, offers }. `held` is shared/legal.mjs notPromoted: such a row is never shown.
 *                    The built page (scripts/gen-tournaments.mjs buildLeague) carries items { key, date, time, home,
 *                    away, homeId, awayId, matchup, venue, city, round, state, price, partners, path, demand } and
 *                    `league: { rounds, clubs, venues, biggest }`; src/components/tournament/LeagueView.tsx draws it.
 *   kinds of wave 3 (non-football; shared/comp-hubs.mjs, scripts/lib/comp-builders.mjs): 'us-season' (NBA, NHL, NFL, college
 *                    basketball), 'circuit' (Formula 1, NASCAR), 'slam' (tennis sessions), 'fight-card', 'round-robin' (Six Nations).
 *                    Their rows are picked from the whole catalog by `pick`, not named by a regex; `whole` aliases name them
 *                    only when the query is nothing else ("nba" yes, "lakers nba" no).
 *
 * FACTS ARE HAND-ENTERED, each with its source. The darts prize money is labelled as last season's until the
 * PDC confirms this season's (PLAN section 13). Nothing here is a price: prices come from the member rows.
 */

/** @typedef {{ route: string, url: string, note?: string }} Official */
/**
 * Which event pages make up a run (PLAN W3-5/W3-6). `artist` is the page's act (regex on the event's artist) or `artistIs` its spellings
 * (compared without case, accents or punctuation); `nameNot` drops products that are not a show ("2 Day Pass"); `venue` / `city`
 * narrow it (Metallica at Sphere); `category` only for the shows (a feed files Olivia Rodrigo in Milan under Theater: a tour is found by the act, not the category); `perDay`: an act plays one show a day, so two rows of one date are one date (a tour), else a date and
 * a start time make a performance (a London show). `minPriced`: fewer fees-included dates than this and the page stays out of the index.
 * @typedef {{ sub: 'tour' | 'residency' | 'show', category?: 'Music' | 'Theater', artist?: RegExp, artistIs?: string[], nameNot?: RegExp,
 *   venue?: RegExp, city?: RegExp, perDay: boolean, minPriced: number, stage?: string }} RunSpec
 */
/** @typedef {{ label: string, value: string, source: string, url: string, note?: string }} Fact */
/**
 * @typedef {{
 *   id: string, name: string, short: string, season: string, kind: 'sessions' | 'knockout' | 'roadshow' | 'oneoff' | 'run' | 'league' | 'us-season' | 'circuit' | 'slam' | 'fight-card' | 'round-robin', plan?: 'cfp' | 'pl-darts' | 'nfl' | 'fight', tag?: string,
 *   window: { from: string, until: string }, venue: string, city: string, tz: string,
 *   aliases: string[], memberTest: RegExp | null, players?: { name: string, short?: string }[],
 *   facts: { format: string, prize: Fact | null, defending: string | null, final: string, faceValue: string | null,
 *     official: Official | null, sources: { name: string, url: string, licence?: string }[] },
 *   pick?: (row: any) => boolean, whole?: (string | RegExp)[], minPriced?: number, series?: string, weekStart?: 'mon' | 'thu', roadTo?: string, courts?: boolean,
 *   sibling?: { path: string, label: string },
 *   relatedPath?: string, queryNot?: RegExp, notWith?: RegExp,
 *   league?: string | { clubs?: string[], country?: string | null, kinds?: string[] | null, tournamentOk?: RegExp | null, notGame?: RegExp | null,
 *     rounds?: { n: number, from: string, until: string }[], floatDates?: boolean, min?: number, entry?: string },
 * }} Tournament
 */

export const ymd = (d) => d.toISOString().slice(0, 10);
export const dayAdd = (iso, n) => ymd(new Date(Date.parse(`${iso}T12:00:00Z`) + n * 864e5));

// ---- darts: the partners' session names -----------------------------------------------------------------
// HelloTickets: "2026/27 Paddy Power World Darts Championship - Evening Session (4x First Round Matches)"
// TicketNetwork: "Paddy Power World Championship - Afternoon Session" (no round)
export const DARTS_ROUNDS = [
  { n: 1, label: 'Round 1', re: /\b(?:first|1st) round\b/i },
  { n: 2, label: 'Round 2', re: /\b(?:second|2nd) round\b/i },
  { n: 3, label: 'Round 3', re: /\b(?:third|3rd) round\b/i },
  { n: 4, label: 'Round 4', re: /\b(?:fourth|4th) round\b/i },
  { n: 5, label: 'Quarter-finals', re: /\bquarter[- ]?finals?\b/i },
  { n: 6, label: 'Semi-finals', re: /\bsemi[- ]?finals?\b/i },
  { n: 7, label: 'Final', re: /\b(?:the )?final\b/i },
];

/**
 * One darts session from its partner name: which part of the day, which round, how many matches.
 * @param {string} name @param {string | null | undefined} [time] "HH:MM" when the name has no part
 * @returns {{ part: 'afternoon' | 'evening' | null, round: number | null, roundLabel: string | null, matches: number | null } | null}
 */
export const parseDartsSession = (name, time = null) => {
  const s = String(name ?? '');
  if (!/\b(?:session|matches?|round|finals?)\b/i.test(s) && !/world (?:darts )?championship/i.test(s)) return null;
  const part = /\bafternoon\b/i.test(s) ? 'afternoon' : /\bevening\b/i.test(s) ? 'evening' : null;
  const inner = s.match(/\(([^)]*)\)/)?.[1] ?? s;
  // "Semi-Finals" must win over the bare "final"; the first four are tested in order, then the knockout rounds
  const hit = DARTS_ROUNDS.slice(0, 6).find((r) => r.re.test(inner)) ?? (/\b(?:the )?final\b/i.test(inner) ? DARTS_ROUNDS[6] : null);
  const m = inner.match(/\b(\d+)\s*x\b/i);
  let p = part;
  if (!p && time && /^\d{1,2}:\d{2}/.test(time)) p = Number(time.slice(0, 2)) < 17 ? 'afternoon' : 'evening';
  return { part: p, round: hit?.n ?? null, roundLabel: hit?.label ?? null, matches: m ? Number(m[1]) : hit?.n === 7 ? 1 : null };
};

/**
 * The World Championship's planned sessions: where HelloTickets' own session names put each round when the
 * 2026-10-19 research read them (docs/waves-2026-10/tournaments.md section 4.2). A partner's listing of the
 * same day and part replaces the plan's round and date (scripts/gen-tournaments.mjs); a slot nobody lists
 * yet stays here as "Not listed yet". `tentative` marks days the research could not square with the
 * PDC's announced Christmas break (24-27 December): they carry "Date not final" until a partner or the PDC
 * confirms them. 37 slots.
 * @returns {{ key: string, date: string, part: 'afternoon' | 'evening', round: number, tentative: boolean }[]}
 */
export const wdcPlan = () => {
  /** @type {{ key: string, date: string, part: 'afternoon' | 'evening', round: number, tentative: boolean }[]} */
  const out = [];
  const add = (date, part, round, tentative = false) => out.push({ key: `${date}-${part === 'afternoon' ? 'a' : 'e'}`, date, part, round, tentative });
  const both = (date, round, tentative = false) => {
    add(date, 'afternoon', round, tentative);
    add(date, 'evening', round, tentative);
  };
  add('2026-12-11', 'evening', 1); // opening night: evening only
  for (let d = 12; d <= 19; d++) both(`2026-12-${d}`, 1);
  for (let d = 20; d <= 23; d++) both(`2026-12-${d}`, 2);
  both('2026-12-27', 3, true);
  both('2026-12-28', 3, true);
  add('2026-12-29', 'afternoon', 3, true);
  add('2026-12-29', 'evening', 4, true);
  both('2026-12-30', 4, true);
  both('2027-01-01', 5);
  add('2027-01-02', 'evening', 6);
  add('2027-01-03', 'evening', 7);
  return out;
};

/**
 * Premier League Darts 2027: the 17 Thursday nights with their cities and arenas (Darts World, 2026-09,
 * checked against BetMGM's list on 2026-10-22). A partner's row on the same date fills the night's price.
 * @returns {{ n: number, date: string, city: string, venue: string }[]}
 */
export const plNights = () =>
  [
    ['2027-02-04', 'Glasgow', 'OVO Hydro'],
    ['2027-02-11', 'Berlin', 'Uber Arena'],
    ['2027-02-18', 'Newcastle', 'Utilita Arena'],
    ['2027-02-25', 'Belfast', 'The O2 Belfast'],
    ['2027-03-04', 'Cardiff', 'Utilita Arena'],
    ['2027-03-11', 'Nottingham', 'Motorpoint Arena'],
    ['2027-03-18', 'Dublin', '3Arena'],
    ['2027-03-25', 'Antwerp', 'AFAS Dome'],
    ['2027-04-01', 'Brighton', 'Brighton Centre'],
    ['2027-04-08', 'Manchester', 'AO Arena'],
    ['2027-04-15', 'Rotterdam', 'Rotterdam Ahoy'],
    ['2027-04-22', 'Liverpool', 'M&S Bank Arena'],
    ['2027-04-29', 'Birmingham', 'Utilita Arena'],
    ['2027-05-06', 'Leeds', 'first direct arena'],
    ['2027-05-13', 'Aberdeen', 'P&J Live'],
    ['2027-05-20', 'Sheffield', 'Utilita Arena'],
    ['2027-05-27', 'London', 'The O2'],
  ].map(([date, city, venue], i) => ({ n: i + 1, date, city, venue }));

/**
 * College Football Playoff 2026-27: the 11 games (fbschedules.com, read 2026-10-22). `bowl` is the name test a
 * partner row must pass; the first round has none (its rows are not matched: sites and teams follow the
 * 6 December Selection Day). `teamsKnown` / `venueKnown`: the date from which the chip "Teams TBD" /
 * "Venue TBD" is dropped (the page still names no teams: names are not hand-entered).
 * @returns {{ key: string, date: string, stage: string, name: string, venue: string, bowl: RegExp | null, teamsKnown: string, venueKnown: string | null }[]}
 */
export const cfpPlan = () => {
  const SEL = '2026-12-06';
  const g = (key, date, stage, name, venue, bowl, teamsKnown, venueKnown = null) => ({ key, date, stage, name, venue, bowl, teamsKnown, venueKnown });
  return [
    g('r1-1', '2026-12-18', 'First round', 'Game 1', '', null, SEL, SEL),
    g('r1-2', '2026-12-19', 'First round', 'Game 2', '', null, SEL, SEL),
    g('r1-3', '2026-12-19', 'First round', 'Game 3', '', null, SEL, SEL),
    g('r1-4', '2026-12-19', 'First round', 'Game 4', '', null, SEL, SEL),
    g('qf-fiesta', '2026-12-30', 'Quarterfinal', 'Fiesta Bowl', 'Glendale, AZ', /\bfiesta\b/i, SEL),
    g('qf-peach', '2027-01-01', 'Quarterfinal', 'Peach Bowl', 'Atlanta, GA', /\bpeach\b/i, SEL),
    g('qf-rose', '2027-01-01', 'Quarterfinal', 'Rose Bowl Game', 'Pasadena, CA', /\brose bowl\b/i, SEL),
    g('qf-cotton', '2027-01-01', 'Quarterfinal', 'Cotton Bowl', 'Arlington, TX', /\bcotton\b/i, SEL),
    g('sf-orange', '2027-01-14', 'Semifinal', 'Orange Bowl', 'Miami Gardens, FL', /\borange bowl\b/i, '2027-01-02'),
    g('sf-sugar', '2027-01-15', 'Semifinal', 'Sugar Bowl', 'New Orleans, LA', /\bsugar bowl\b/i, '2027-01-02'),
    g('final', '2027-01-25', 'Final', 'National Championship', 'Allegiant Stadium, Las Vegas, NV', /\bnational championship\b|\bcfp championship\b/i, '2027-01-16'),
  ];
};

/**
 * Fury vs Joshua: the one fight (Netflix Tudum announcement, read 2026-10-23: Friday 11 December 2026, Principality
 * Stadium, Cardiff). No start time is published yet,
 * so the item carries no time. The undercard is not announced: the page says "Card may change".
 */
export const fightPlan = () => ({
  key: 'main',
  date: '2026-12-11',
  stage: 'Main event',
  name: 'Tyson Fury vs Anthony Joshua',
  venue: 'Principality Stadium, Cardiff',
});

/**
 * NFL playoffs 2026-27 and Super Bowl LXI: 13 slots. Round windows are Wikipedia's "2026 NFL season" (read 2026-10-23):
 * Wild Card Jan 16-18, Divisional Jan 23-24, Conference Championships Jan 31, Super Bowl LXI Feb 14 at SoFi Stadium,
 * Inglewood; the regular season is scheduled to end Jan 10. The day of each wild-card and divisional game is not
 * announced, so those slots carry the round's first day plus "Date not final" until a partner lists the game (its
 * own date then replaces ours). `from`/`until` is the window a partner row's date must fall in; `teamsKnown` /
 * `venueKnown` is the date from which "Teams TBD" / "Venue TBD" is dropped (venues follow the seeds).
 * @returns {{ key: string, round: string, name: string, date: string, from: string, until: string, venue: string, test: RegExp, teamsKnown: string, venueKnown: string | null }[]}
 */
export const nflPlan = () => {
  const out = [];
  const add = (key, round, name, date, until, venue, test, teamsKnown, venueKnown) => out.push({ key, round, name, date, from: date, until, venue, test, teamsKnown, venueKnown });
  for (let i = 1; i <= 6; i++) add(`wc${i}`, 'Wild Card', `Wild Card game ${i}`, '2027-01-16', '2027-01-18', '', /wild[- ]?card/i, '2027-01-11', '2027-01-11');
  for (let i = 1; i <= 4; i++) add(`div${i}`, 'Divisional round', `Divisional game ${i}`, '2027-01-23', '2027-01-24', '', /divisional/i, '2027-01-19', '2027-01-19');
  add('afc', 'Conference Championship', 'AFC Championship', '2027-01-31', '2027-01-31', '', /\bafc\b/i, '2027-01-25', '2027-01-25');
  add('nfc', 'Conference Championship', 'NFC Championship', '2027-01-31', '2027-01-31', '', /\bnfc\b/i, '2027-01-25', '2027-01-25');
  add('sb', 'Super Bowl', 'Super Bowl LXI', '2027-02-14', '2027-02-14', 'SoFi Stadium, Inglewood, CA', /super bowl/i, '2027-02-01', null);
  return out;
};

// Each bowl only with 'cfp' or a year, or as 'X bowl game'; never the stadium alone (the Rose Bowl and the Cotton
// Bowl host concerts all year, so those two get no year variants).
const CFP_BOWL_ALIASES = [
  ...['fiesta', 'peach', 'orange', 'sugar'].flatMap((b) => [`${b} bowl cfp`, `cfp ${b} bowl`, `${b} bowl game`, `${b} bowl 2026`, `${b} bowl 2027`]),
  ...['rose', 'cotton'].flatMap((b) => [`${b} bowl cfp`, `cfp ${b} bowl`, `${b} bowl game`]),
];

/**
 * STATIC DATA (hand-entered, 2026-10): the PDC Order of Merit top ~36 as the likely World Championship field.
 * The real draw replaces this once the PDC publishes it (PLAN W2-3: "top 32 now, the full field after the
 * draw"). `short` is the surname people search by, given only where it is distinctive on its own: "price",
 * "wright", "smith", "rock" are not, so those players match by full name only.
 */
const WDC_PLAYERS = [
  ['Luke Littler', 'littler'], ['Luke Humphries', 'humphries'], ['Michael van Gerwen', 'van gerwen'], ['Gerwyn Price'],
  ['Gian van Veen', 'van veen'], ['Jonny Clayton'], ['Rob Cross'], ['Nathan Aspinall', 'aspinall'], ['Stephen Bunting', 'bunting'],
  ['Chris Dobey', 'dobey'], ['Josh Rock'], ['Dave Chisnall', 'chisnall'], ['Danny Noppert', 'noppert'], ['Peter Wright'],
  ['Damon Heta', 'heta'], ['Ross Smith'], ['Mike De Decker', 'de decker'], ['Ryan Searle', 'searle'], ['Martin Lukeman', 'lukeman'],
  ['James Wade'], ['Dimitri Van den Bergh', 'van den bergh'], ['Jermaine Wattimena', 'wattimena'], ['Ritchie Edhouse', 'edhouse'],
  ['Gary Anderson'], ['Michael Smith'], ['Krzysztof Ratajski', 'ratajski'], ['Andrew Gilding', 'gilding'], ['Cameron Menzies', 'menzies'],
  ['Ryan Joyce'], ['Luke Woodhouse'], ['Callan Rydz', 'rydz'], ['Raymond van Barneveld', 'van barneveld'], ['Martin Schindler', 'schindler'],
  ['Dirk van Duijvenbode', 'van duijvenbode'], ['Scott Williams'], ['Kim Huybrechts', 'huybrechts'],
].map(([name, short]) => ({ name, ...(short ? { short } : {}) }));

// ---- wave 3: non-football competition hubs (owner 2026-10-23) ---------------------------------------------
/** A wave-3 entry: no regex member test (rows are picked), no official route, face value or retail link (owner, 2026-10-22). */
const comp = (o) => ({
  city: '',
  tz: 'America/New_York',
  aliases: [],
  memberTest: null,
  minPriced: 6,
  ...o,
  facts: { prize: null, defending: null, faceValue: null, official: null, ...o.facts },
});
const WIKI = (name, path) => ({ name: `Wikipedia: ${name}`, url: `https://en.wikipedia.org/wiki/${path}`, licence: 'CC BY-SA 4.0' });

// ---- the registry -----------------------------------------------------------------------------------------
/** @type {Tournament[]} */
export const TOURNAMENTS = [
  {
    id: 'pdc-wdc-2026-27',
    tag: 'Darts',
    name: 'PDC World Darts Championship 2026/27',
    short: 'World Darts Championship',
    season: '2026/27',
    kind: 'sessions',
    window: { from: '2026-12-11', until: '2027-01-03' },
    venue: 'Alexandra Palace (Great Hall)',
    city: 'London',
    tz: 'Europe/London',
    // darts words only: 'ally pally' alone is the venue (concerts all year), 'wdc' alone is ambiguous
    aliases: ['pdc worlds', 'pdc world darts', 'pdc world darts championship', 'pdc world championship', 'world darts championship', 'wk darts', 'ally pally darts', 'alexandra palace darts', 'darts world championship', 'paddy power world darts championship', 'world championship darts', 'pdc darts', 'pdc'],
    players: WDC_PLAYERS,
    memberTest: /\bworld darts championship\b|\bpaddy power world championship\b|\bpdc world (?:darts )?championship\b/i,
    facts: {
      format: '128 players, all starting in round 1. Sets: best of 5 in rounds 1 and 2, best of 7 in rounds 3 and 4, best of 9 in the quarter-finals, best of 11 in the semi-finals, best of 13 in the final.',
      prize: {
        label: 'Prize money',
        value: '£5,000,000 in total; £1,000,000 to the winner',
        source: 'PDC announcement of 31 March 2025 (2025/26 prize money; 2026/27 not yet confirmed)',
        url: 'https://www.pdc.tv/news/2025/march/31/biggest-prize-money-increase-pdc-history-confirmed',
        note: '2025/26 prize money; 2026/27 not yet confirmed',
      },
      defending: 'Luke Littler (beat Gian van Veen 7-1 in the 2026 final)',
      final: 'Sunday 3 January 2027, evening, Alexandra Palace',
      // No official route, face value or retail links (owner, 2026-10-22): visitors go to our partners only.
      faceValue: null,
      official: null,
      sources: [
        { name: 'Wikipedia: 2027 PDC World Darts Championship', url: 'https://en.wikipedia.org/wiki/2027_PDC_World_Darts_Championship', licence: 'CC BY-SA 4.0' },
        { name: 'Sky Sports: 2026/27 dates confirmed (6 August 2026)', url: 'https://www.skysports.com/darts/news/12040/13570429/world-darts-championship-2026-27-dates-confirmed-for-alexandra-palace-with-sunday-final-in-great-hall-for-first-time' },
      ],
    },
  },
  {
    id: 'world-series-2026',
    name: 'World Series 2026',
    short: 'World Series',
    season: '2026',
    kind: 'knockout',
    window: { from: '2026-10-23', until: '2026-10-31' },
    venue: 'Milwaukee and Tampa Bay',
    city: '',
    tz: 'America/New_York',
    aliases: ['world series', 'mlb world series', 'world series 2026'],
    memberTest: null,
    facts: {
      format: 'Best of seven. The club with the better record hosts games 1, 2, 6 and 7; the other club hosts games 3, 4 and 5. Games 5 to 7 are played only if the series is not decided earlier.',
      prize: null,
      defending: null,
      final: 'Game 7 (if needed): Saturday 31 October 2026',
      faceValue: null,
      official: null,
      sources: [{ name: 'TixInfo World Series record (price index)', url: 'https://tixinfo.com/price-index/world-series' }],
    },
    relatedPath: '/price-index/world-series',
  },
  {
    id: 'pl-darts-2027',
    name: 'Premier League Darts 2027',
    short: 'Premier League Darts',
    season: '2027',
    kind: 'roadshow',
    plan: 'pl-darts',
    tag: 'Darts',
    window: { from: '2027-02-04', until: '2027-05-27' },
    venue: 'Arenas in the UK, Ireland, Germany, Belgium and the Netherlands',
    city: '',
    tz: 'Europe/London',
    // whole darts phrases only: plain 'premier league' is football
    aliases: ['premier league darts', 'pl darts', 'darts rotterdam', 'darts antwerp'],
    memberTest: /\bpremier league darts\b/i,
    facts: {
      format: '17 Thursday nights, a different arena each week. The top four players from the league phase reach Finals Night.',
      prize: {
        label: 'Prize money',
        value: 'Not yet confirmed for 2027',
        source: 'Darts World: Premier League schedule for 2027 (no 2027 prize fund is stated)',
        url: 'https://dartsworld.com/2026/09/premier-league-schedule-for-2027-confirmed/',
        note: '2027 prize money not yet confirmed',
      },
      defending: 'Luke Littler (2026 champion)',
      final: 'Thursday 27 May 2027, Finals Night, The O2, London',
      faceValue: null,
      official: null,
      sources: [
        { name: 'Darts World: Premier League schedule for 2027 confirmed (September 2026)', url: 'https://dartsworld.com/2026/09/premier-league-schedule-for-2027-confirmed/' },
        { name: 'Sky Sports: Premier League Darts 2027 dates and venues confirmed', url: 'https://www.skysports.com/darts/news/13591406/premier-league-darts-2027-dates-and-venues-confirmed-glasgow-hosts-opener-finals-night-in-london-as-luke-littler-defends-title' },
      ],
    },
  },
  {
    id: 'cfp-2026-27',
    name: 'College Football Playoff 2026-27',
    short: 'College Football Playoff',
    season: '2026-27',
    kind: 'knockout',
    plan: 'cfp',
    tag: 'College football',
    window: { from: '2026-12-18', until: '2027-01-25' },
    venue: 'Various venues',
    city: '',
    tz: 'America/New_York',
    // 'rose bowl' / 'cotton bowl' alone are venues with concerts: only the game phrases (CFP_BOWL_ALIASES)
    aliases: ['cfp', 'college football playoff', 'college football playoffs', 'national championship game', 'cfp national championship', ...CFP_BOWL_ALIASES],
    memberTest: /\bcfp\b|\bcollege football playoff\b/i,
    facts: {
      format: '12 teams, 11 games: four first-round games at campus sites (Dec 18 and 19), four quarterfinals at bowl games, two semifinals and the national championship. Sites and teams are set on Selection Day, Sunday 6 December.',
      prize: null,
      defending: null,
      final: 'Monday 25 January 2027, Allegiant Stadium, Las Vegas',
      faceValue: null,
      official: null,
      sources: [
        { name: 'fbschedules.com: 2026-27 College Football Playoff schedule and kickoff times', url: 'https://fbschedules.com/college-football-playoff-sets-2026-27-schedule-kickoff-times-tv-designations/' },
        { name: 'Sports Illustrated: 2026-27 College Football Playoff schedule', url: 'https://www.si.com/fannation/college/cfb-hq/schedules/2026-27-college-football-playoff-full-schedule-dates-tv-coverage-host-venues' },
      ],
    },
  },
  {
    id: 'fury-joshua',
    tag: 'Boxing',
    name: 'Fury vs Joshua',
    short: 'Fury vs Joshua',
    season: '2026',
    kind: 'oneoff',
    plan: 'fight',
    window: { from: '2026-12-11', until: '2026-12-11' },
    venue: 'Principality Stadium, Cardiff',
    city: 'Cardiff',
    tz: 'Europe/London',
    aliases: ['fury joshua', 'fury vs joshua', 'fury v joshua', 'joshua fury', 'joshua vs fury', 'fury aj', 'tyson fury anthony joshua', 'fury vs aj'],
    memberTest: /\bfury\b.*\bjoshua\b|\bjoshua\b.*\bfury\b/i,
    facts: {
      format: 'A heavyweight fight between Tyson Fury and Anthony Joshua, live on Netflix. The undercard has not been announced.',
      prize: null,
      defending: null,
      final: 'Friday 11 December 2026, Principality Stadium, Cardiff. No start time is published yet.',
      // No official route, face value or retail links (owner, 2026-10-22): visitors go to our partners only.
      faceValue: null,
      official: null,
      sources: [
        { name: 'Netflix Tudum: Tyson Fury vs. Anthony Joshua is official for Dec. 11', url: 'https://www.netflix.com/tudum/features/tyson-fury-anthony-joshua-live-on-netflix' },
        { name: "What's on Netflix: Fury vs. Joshua on Netflix in December 2026", url: 'https://www.whats-on-netflix.com/news/tyson-fury-anthony-joshua-netflix-boxing-match-2026/' },
      ],
    },
  },
  {
    id: 'road-to-sb-lxi',
    tag: 'NFL',
    name: 'NFL Playoffs and Super Bowl LXI',
    short: 'NFL playoffs',
    season: '2026-27',
    kind: 'knockout',
    plan: 'nfl',
    window: { from: '2027-01-16', until: '2027-02-14' },
    venue: 'Various venues; Super Bowl LXI at SoFi Stadium, Inglewood',
    city: '',
    tz: 'America/New_York',
    // whole phrases: plain 'super bowl' and 'nfl' are everything from halftime shows to preseason
    aliases: ['super bowl lxi', 'super bowl 61', 'super bowl 2027', 'nfl playoffs', 'nfl playoff', 'nfl wild card', 'nfl divisional round', 'afc championship', 'nfc championship', 'road to the super bowl'],
    memberTest: /\bsuper bowl (?:lxi|61)\b|\bnfl (?:playoffs?|wild[- ]?card|divisional)\b|\b(?:afc|nfc) championship\b/i,
    facts: {
      format: '14 teams, 13 games: six wild-card games (Jan 16 to 18), four divisional games (Jan 23 and 24), the AFC and NFC championship games (Jan 31) and Super Bowl LXI (Feb 14). Teams and host cities are set when the regular season ends on Jan 10; the day of each wild-card and divisional game is announced after that.',
      prize: null,
      defending: null,
      final: 'Sunday 14 February 2027, SoFi Stadium, Inglewood, California',
      faceValue: null,
      official: null,
      sources: [
        { name: 'Wikipedia: 2026 NFL season', url: 'https://en.wikipedia.org/wiki/2026_NFL_season', licence: 'CC BY-SA 4.0' },
        { name: 'Wikipedia: Super Bowl LXI', url: 'https://en.wikipedia.org/wiki/Super_Bowl_LXI', licence: 'CC BY-SA 4.0' },
      ],
    },
  },
  // ---- football competitions (kind 'league'; wave w3-comp-football). One contiguous block: other builders add
  // their entries above or below it. Club lists are the 2026/27 members as the partners' schedules pair them
  // (each club hosts the other 19 / 17), checked against the catalog on 2026-10-23; the page counts only games
  // whose BOTH sides are on the list, in the league's own country, so cups and friendlies stay out.
  {
    id: 'ucl-2026-27',
    tag: 'Soccer',
    name: 'UEFA Champions League 2026/27',
    short: 'Champions League',
    season: '2026/27',
    kind: 'league',
    window: { from: '2026-09-15', until: '2027-06-05' },
    venue: 'Clubs across Europe',
    city: '',
    tz: 'Europe/Paris',
    aliases: ['champions league', 'uefa champions league', 'ucl', 'champions league tickets', 'champions league final', 'ucl final'],
    queryNot: /\b(?:women|womens|women s|feminin|femenina|frauen|futsal|hockey|youth|u19|u21)\b/,
    memberTest: /^uefa champions league\b/i,
    league: {
      country: null,
      kinds: ['soccer'],
      notGame: /women|futsal|youth/i,
      // UEFA calendar, matchdays 4 to 8 (the earlier ones are played); every listed game date falls inside one
      rounds: [
        { n: 4, from: '2026-11-03', until: '2026-11-05' },
        { n: 5, from: '2026-11-24', until: '2026-11-26' },
        { n: 6, from: '2026-12-08', until: '2026-12-10' },
        { n: 7, from: '2027-01-19', until: '2027-01-21' },
        { n: 8, from: '2027-01-27', until: '2027-01-28' },
      ],
      floatDates: false,
      entry: 'UEFA and the clubs sell personal tickets; a resold ticket can be refused at the gate.',
    },
    facts: {
      format: '36 clubs play a league phase of eight games each, then knockout rounds from February: play-offs, round of 16, quarter-finals, semi-finals and the final. Knockout games appear here once the draws are made and a marketplace lists them.',
      prize: null,
      defending: null,
      final: 'Saturday 5 June 2027, Estadio Metropolitano, Madrid',
      faceValue: null,
      official: null,
      sources: [
        { name: 'Wikipedia: 2026–27 UEFA Champions League', url: 'https://en.wikipedia.org/wiki/2026%E2%80%9327_UEFA_Champions_League', licence: 'CC BY-SA 4.0' },
        { name: 'Manchester City: UEFA Champions League 2026/27 explainer (dates)', url: 'https://www.mancity.com/news/mens/uefa-champions-league-2026-27-explainer-63919465' },
        { name: 'SI: UEFA confirms the 2027 Champions League final venues', url: 'https://www.si.com/soccer/uefa-confirm-2027-champions-league-final-venues' },
      ],
    },
  },
  {
    id: 'uel-2026-27',
    tag: 'Soccer',
    name: 'UEFA Europa League 2026/27',
    short: 'Europa League',
    season: '2026/27',
    kind: 'league',
    window: { from: '2026-09-17', until: '2027-05-26' },
    venue: 'Clubs across Europe',
    city: '',
    tz: 'Europe/Paris',
    aliases: ['europa league', 'uefa europa league', 'uel', 'europa league tickets', 'europa league final'],
    queryNot: /\b(?:women|womens|women s|feminin|frauen|conference|futsal|youth|u19|u21)\b/,
    memberTest: /^uefa europa league\b/i,
    league: {
      country: null,
      kinds: ['soccer'],
      notGame: /women|futsal|youth/i,
      // Thursday matchdays 4 to 8 (the earlier ones are played)
      rounds: [
        { n: 4, from: '2026-11-05', until: '2026-11-05' },
        { n: 5, from: '2026-11-26', until: '2026-11-26' },
        { n: 6, from: '2026-12-10', until: '2026-12-10' },
        { n: 7, from: '2027-01-21', until: '2027-01-21' },
        { n: 8, from: '2027-01-28', until: '2027-01-28' },
      ],
      floatDates: false,
      entry: 'UEFA and the clubs sell personal tickets; a resold ticket can be refused at the gate.',
    },
    facts: {
      format: '36 clubs play a league phase of eight games each, then knockout rounds from February and the final. Knockout games appear here once the draws are made and a marketplace lists them.',
      prize: null,
      defending: null,
      final: 'Wednesday 26 May 2027, Frankfurt Arena',
      faceValue: null,
      official: null,
      sources: [
        { name: 'Wikipedia: 2026–27 UEFA Europa League', url: 'https://en.wikipedia.org/wiki/2026%E2%80%9327_UEFA_Europa_League', licence: 'CC BY-SA 4.0' },
        { name: 'Wikipedia: 2027 UEFA Europa League final', url: 'https://en.wikipedia.org/wiki/2027_UEFA_Europa_League_final', licence: 'CC BY-SA 4.0' },
      ],
    },
  },
  {
    id: 'premier-league-2026-27',
    tag: 'Soccer',
    name: 'Premier League 2026/27',
    short: 'Premier League',
    season: '2026/27',
    kind: 'league',
    window: { from: '2026-08-01', until: '2027-05-30' },
    venue: 'Clubs across England',
    city: '',
    tz: 'Europe/London',
    // football only: "premier league darts" is the darts entry's longer alias (the longest alias wins), snooker and rugby have their own
    aliases: ['premier league', 'english premier league', 'epl', 'premier league football', 'premier league soccer', 'premier league tickets'],
    queryNot: /\b(?:darts|snooker|rugby|cricket|lacrosse|handball|women|womens|women s|u21|u18|international cup|championship)\b/,
    memberTest: null,
    league: {
      country: 'GB',
      kinds: ['soccer'],
      tournamentOk: /^premier league$/i,
      notGame: /friendly|testimonial|women|u21|u18/i,
      floatDates: true,
      entry: 'Clubs sell personal tickets; a resold ticket can be refused at the gate.',
      clubs: [
        'Arsenal', 'Aston Villa', 'Bournemouth|AFC Bournemouth', 'Brentford', 'Brighton & Hove Albion|Brighton', 'Chelsea', 'Coventry City|Coventry', 'Crystal Palace', 'Everton', 'Fulham',
        'Hull City|Hull', 'Ipswich Town|Ipswich', 'Leeds United|Leeds', 'Liverpool', 'Manchester City|Man City', 'Manchester United|Man United|Man Utd', 'Newcastle United|Newcastle',
        'Nottingham Forest', 'Sunderland', 'Tottenham Hotspur|Tottenham|Spurs',
      ],
    },
    facts: {
      format: '20 clubs; each plays the others once at home and once away, 38 games in all. The dates in this list are the marketplaces\' and can move when broadcasters choose their games.',
      prize: null,
      defending: null,
      final: 'Last game in our listings: {last}',
      faceValue: null,
      official: null,
      sources: [{ name: 'Wikipedia: 2026–27 Premier League', url: 'https://en.wikipedia.org/wiki/2026%E2%80%9327_Premier_League', licence: 'CC BY-SA 4.0' }],
    },
  },
  {
    id: 'la-liga-2026-27',
    tag: 'Soccer',
    name: 'La Liga 2026/27',
    short: 'La Liga',
    season: '2026/27',
    kind: 'league',
    window: { from: '2026-08-01', until: '2027-05-30' },
    venue: 'Clubs across Spain',
    city: '',
    tz: 'Europe/Madrid',
    aliases: ['la liga', 'laliga', 'la liga tickets', 'laliga tickets', 'spanish la liga'],
    queryNot: /\b(?:liga 2|laliga 2|hypermotion|segunda|women|womens|women s|femenina)\b/,
    memberTest: null,
    league: {
      country: 'ES',
      kinds: ['soccer'],
      tournamentOk: /^la ?liga$/i,
      notGame: /friendly|testimonial|women|femenin/i,
      floatDates: true,
      entry: 'Clubs sell personal tickets; a resold ticket can be refused at the gate.',
      clubs: [
        'Athletic Club|Athletic Bilbao|Athletic Club Bilbao', 'Atlético Madrid|Atletico de Madrid|Atletico Madrid', 'Osasuna', 'Deportivo Alavés|Alaves|Alavés', 'Deportivo La Coruña|Deportivo A Coruna|Deportivo La Coruna|Deportivo',
        'Elche', 'Barcelona', 'Getafe', 'Levante', 'Málaga|Malaga', 'Celta Vigo|Celta de Vigo', 'Espanyol', 'Racing Santander|Racing de Santander', 'Rayo Vallecano', 'Real Betis', 'Real Madrid', 'Real Sociedad',
        'Sevilla', 'Valencia', 'Villarreal',
      ],
    },
    facts: {
      format: '20 clubs; each plays the others once at home and once away, 38 games in all. The league sets dates and kick-off times about a month ahead, so a game without a kick-off time can still move.',
      prize: null,
      defending: null,
      final: 'Last game in our listings: {last}',
      faceValue: null,
      official: null,
      sources: [{ name: 'Wikipedia: 2026–27 La Liga', url: 'https://en.wikipedia.org/wiki/2026%E2%80%9327_La_Liga', licence: 'CC BY-SA 4.0' }],
    },
  },
  {
    id: 'bundesliga-2026-27',
    tag: 'Soccer',
    name: 'Bundesliga 2026/27',
    short: 'Bundesliga',
    season: '2026/27',
    kind: 'league',
    window: { from: '2026-08-01', until: '2027-05-22' },
    venue: 'Clubs across Germany',
    city: '',
    tz: 'Europe/Berlin',
    aliases: ['bundesliga', 'german bundesliga', 'bundesliga tickets', 'bundesliga football'],
    queryNot: /\b(?:2 bundesliga|bundesliga 2|zweite|2 liga|women|womens|women s|frauen|basketball|handball|volleyball)\b/,
    memberTest: null,
    league: {
      country: 'DE',
      kinds: ['soccer'],
      tournamentOk: /^bundesliga$/i,
      notGame: /friendly|testimonial|women|frauen/i,
      floatDates: true,
      entry: 'Clubs sell personal tickets; a resold ticket can be refused at the gate.',
      clubs: [
        'Bayern Munich|Bayern München', 'Borussia Dortmund|Dortmund', 'Bayer Leverkusen|Leverkusen', 'RB Leipzig', 'Eintracht Frankfurt', 'VfB Stuttgart', 'Borussia Mönchengladbach|Gladbach', 'SC Freiburg',
        '1. FC Köln|FC Koln|Köln', '1. FSV Mainz 05|FSV Mainz 05|Mainz', 'Hamburger SV', 'FC Augsburg', 'Union Berlin', 'Werder Bremen', 'Schalke 04', 'SC Paderborn 07|Paderborn', 'SV Elversberg|Elversberg',
        'TSG Hoffenheim|Hoffenheim',
      ],
    },
    facts: {
      format: '18 clubs; each plays the others once at home and once away, 34 games in all. A round is played over a weekend; kick-off times follow the broadcasters.',
      prize: null,
      defending: null,
      final: 'Last game in our listings: {last}',
      faceValue: null,
      official: null,
      sources: [{ name: 'Wikipedia: 2026–27 Bundesliga', url: 'https://en.wikipedia.org/wiki/2026%E2%80%9327_Bundesliga', licence: 'CC BY-SA 4.0' }],
    },
  },
  {
    id: 'eredivisie-2026-27',
    tag: 'Soccer',
    name: 'Eredivisie 2026/27',
    short: 'Eredivisie',
    season: '2026/27',
    kind: 'league',
    window: { from: '2026-08-01', until: '2027-05-16' },
    venue: 'Clubs across the Netherlands',
    city: '',
    tz: 'Europe/Amsterdam',
    aliases: ['eredivisie', 'dutch eredivisie', 'dutch league', 'eredivisie tickets', 'klassieker', 'de klassieker'],
    queryNot: /\b(?:vrouwen|women|womens|women s|eerste divisie|keuken kampioen)\b/,
    memberTest: null,
    league: {
      country: 'NL',
      kinds: ['soccer'],
      tournamentOk: /^eredivisie$/i,
      notGame: /friendly|testimonial|vrouwen|women|jong/i,
      floatDates: true,
      min: 12,
      entry: 'Club tickets are personal; high-demand games can need a club card or membership, and a resold ticket can be refused at the gate.',
      clubs: [
        'Ajax|AFC Ajax', 'PSV|PSV Eindhoven', 'Feyenoord', 'AZ|AZ Alkmaar', 'FC Twente|Twente', 'FC Utrecht|Utrecht', 'FC Groningen|Groningen', 'SC Heerenveen|Heerenveen', 'NEC Nijmegen|NEC',
        'Go Ahead Eagles', 'PEC Zwolle', 'Sparta Rotterdam', 'Fortuna Sittard', 'Willem II', 'ADO Den Haag', 'SC Cambuur|Cambuur', 'SC Telstar|Telstar', 'SBV Excelsior|Excelsior',
      ],
    },
    facts: {
      format: '18 clubs; each plays the others once at home and once away, 34 games in all. The games listed here are the ones the marketplaces list, mostly the big clubs\' home games; dates are provisional until the league confirms them.',
      prize: null,
      defending: null,
      final: 'Last game in our listings: {last}',
      faceValue: null,
      official: null,
      sources: [{ name: 'Wikipedia: 2026–27 Eredivisie', url: 'https://en.wikipedia.org/wiki/2026%E2%80%9327_Eredivisie', licence: 'CC BY-SA 4.0' }],
    },
  },
  {
    id: 'mls-2026',
    tag: 'Soccer',
    name: 'MLS 2026',
    short: 'MLS',
    season: '2026',
    kind: 'league',
    window: { from: '2026-02-01', until: '2026-12-31' },
    venue: 'Clubs across the United States and Canada',
    city: '',
    tz: 'America/New_York',
    aliases: ['mls', 'major league soccer', 'mls cup', 'mls playoffs', 'mls cup playoffs', 'mls tickets'],
    queryNot: /\b(?:next pro|women|womens|women s|nwsl|mls next)\b/,
    memberTest: null,
    league: {
      country: null,
      kinds: ['mls', 'soccer'],
      tournamentOk: /\bmls\b|major league soccer/i,
      notGame: /friendly|exhibition|all.?star|leagues cup|women|next pro/i,
      floatDates: false,
      min: 12,
      clubs: [
        'Atlanta United|Atlanta United FC', 'Austin FC', 'CF Montréal|CF Montreal|Montreal', 'Charlotte FC', 'Chicago Fire|Chicago Fire FC', 'FC Cincinnati', 'Colorado Rapids', 'Columbus Crew', 'D.C. United|DC United',
        'FC Dallas', 'Houston Dynamo', 'Inter Miami|Inter Miami CF', 'LA Galaxy', 'LAFC|Los Angeles FC|Los Angeles Football Club', 'Minnesota United|Minnesota United FC', 'Nashville SC', 'New England Revolution',
        'New York City FC|NYCFC', 'New York Red Bulls|RBNY|Red Bull New York', 'Orlando City|Orlando City SC', 'Philadelphia Union', 'Portland Timbers', 'Real Salt Lake', 'San Diego FC', 'San Jose Earthquakes',
        'Seattle Sounders|Seattle Sounders FC', 'Sporting Kansas City', 'St. Louis City|St Louis CITY SC', 'Toronto FC', 'Vancouver Whitecaps|Vancouver Whitecaps FC',
      ],
    },
    facts: {
      format: '30 clubs in two conferences; the leading clubs of each conference reach the MLS Cup Playoffs. Playoff games appear here once a marketplace lists them.',
      prize: null,
      defending: null,
      final: 'Last game in our listings: {last}',
      faceValue: null,
      official: null,
      sources: [{ name: 'Wikipedia: 2026 Major League Soccer season', url: 'https://en.wikipedia.org/wiki/2026_Major_League_Soccer_season', licence: 'CC BY-SA 4.0' }],
    },
    relatedPath: '/league/mls',
  },

  // ---- wave 3 (owner 2026-10-23): US season hubs, race weekends, tennis sessions, fight cards, Six Nations ----
  comp({
    id: 'nba-2026-27', tag: 'NBA', name: 'NBA 2026-27 Season', short: 'NBA', season: '2026-27', kind: 'us-season', league: 'nba',
    window: { from: '2026-10-20', until: '2027-06-30' }, venue: 'Arenas across the United States and Canada', pick: leaguePicker('nba'), whole: ['nba'],
    sibling: { path: '/league/nba', label: 'Every NBA game' },
    facts: {
      format: 'Each club plays 82 regular-season games. The playoffs, the NBA Cup knockout games and the Finals follow; the league sets their dates, and a game that may not be played is marked.',
      final: 'Games listed through {last}',
      sources: [WIKI('2026–27 NBA season', '2026%E2%80%9327_NBA_season')],
    },
  }),
  comp({
    id: 'nhl-2026-27', tag: 'NHL', name: 'NHL 2026-27 Season', short: 'NHL', season: '2026-27', kind: 'us-season', league: 'nhl',
    window: { from: '2026-10-07', until: '2027-06-30' }, venue: 'Arenas across the United States and Canada', pick: leaguePicker('nhl'), whole: ['nhl'],
    sibling: { path: '/league/nhl', label: 'Every NHL game' },
    facts: {
      format: 'Each club plays 82 regular-season games before the Stanley Cup playoffs. Playoff dates are set by the league, and a game that may not be played is marked.',
      final: 'Games listed through {last}',
      sources: [WIKI('2026–27 NHL season', '2026%E2%80%9327_NHL_season')],
    },
  }),
  comp({
    id: 'nfl-2026', tag: 'NFL', name: 'NFL 2026 Season', short: 'NFL', season: '2026', kind: 'us-season', league: 'nfl', weekStart: 'thu',
    window: { from: '2026-09-10', until: '2027-02-14' }, venue: 'Stadiums across the United States', pick: leaguePicker('nfl'), whole: ['nfl'],
    sibling: { path: '/league/nfl', label: 'Every NFL game' }, roadTo: '/tournament/road-to-sb-lxi',
    facts: {
      format: 'Each club plays 17 regular-season games over 18 weeks. The playoffs lead to Super Bowl LXI on 14 February 2027; playoff games that may not be played are marked.',
      final: 'Super Bowl LXI: Sunday 14 February 2027',
      sources: [WIKI('2026 NFL season', '2026_NFL_season')],
    },
  }),
  comp({
    id: 'ncaab-2026-27', tag: 'College basketball', name: 'College Basketball 2026-27 Season', short: 'College basketball', season: '2026-27', kind: 'us-season', league: 'ncaa_basketball',
    window: { from: '2026-10-23', until: '2027-04-05' }, venue: 'Campus arenas and neutral courts across the United States', pick: leaguePicker('ncaa_basketball'),
    whole: ['college basketball', 'ncaa basketball', 'ncaab', 'ncaa mens basketball'],
    sibling: { path: '/league/ncaa_basketball', label: 'Every college basketball game' },
    facts: {
      format: "Men's college basketball: exhibitions and early-season games, then conference play, conference tournaments and the NCAA tournament in March and April.",
      final: 'Games listed through {last}',
      sources: [WIKI("2026–27 NCAA Division I men's basketball season", '2026%E2%80%9327_NCAA_Division_I_men%27s_basketball_season')],
    },
  }),
  comp({
    id: 'f1-2026-27', tag: 'Formula 1', name: 'Formula 1 Race Weekends 2026-27', short: 'Formula 1', season: '2026-27', kind: 'circuit', series: 'f1',
    window: { from: '2026-10-23', until: '2027-12-31' }, venue: 'Circuits in North America, Europe, Asia, the Middle East and Australia', pick: pickF1, whole: ['f1', 'formula 1', 'formula one', 'formula1', 'f1 races', 'f1 grand prix'],
    facts: {
      format: 'A race weekend runs Friday to Sunday: practice on Friday, qualifying on Saturday, the race on Sunday (sprint weekends differ). Marketplaces list single days and multi-day passes; this page puts them side by side per weekend.',
      final: 'Last weekend listed: {last}',
      sources: [WIKI('2027 Formula One World Championship', '2027_Formula_One_World_Championship'), WIKI('2026 Formula One World Championship', '2026_Formula_One_World_Championship')],
    },
  }),
  comp({
    id: 'nascar-2026-27', tag: 'NASCAR', name: 'NASCAR Race Weekends 2026-27', short: 'NASCAR', season: '2026-27', kind: 'circuit', series: 'nascar',
    window: { from: '2026-10-23', until: '2027-11-07' }, venue: 'Speedways and road courses across the United States', pick: pickNascar, whole: ['nascar', 'nascar races', 'nascar cup series', 'nascar cup'],
    facts: {
      format: "A NASCAR weekend is up to three races at one track, usually the Truck Series on Friday, the O'Reilly Series on Saturday and the Cup Series on Sunday; the days shown are the marketplaces' own.",
      final: 'Last weekend listed: {last}',
      sources: [WIKI('2027 NASCAR Cup Series', '2027_NASCAR_Cup_Series'), WIKI('2026 NASCAR Cup Series', '2026_NASCAR_Cup_Series')],
    },
  }),
  comp({
    id: 'us-open-tennis-2027', tag: 'Tennis', name: 'US Open Tennis 2027', short: 'US Open', season: '2027', kind: 'slam',
    window: { from: '2027-08-24', until: '2027-09-12' }, venue: 'USTA Billie Jean King National Tennis Center', city: 'New York', pick: pickTennis(/\bus open tennis\b/i), whole: ['us open tennis', 'us open tennis championships'],
    facts: {
      format: 'Reserved sessions in Arthur Ashe Stadium (a daytime and an evening session on most days) and grounds passes. Sessions are numbered by the marketplaces; the order of play is set by the tournament.',
      final: 'Last date listed: {last}',
      sources: [WIKI('2027 US Open (tennis)', '2027_US_Open_(tennis)')],
    },
  }),
  comp({
    id: 'national-bank-open-2027', tag: 'Tennis', name: 'National Bank Open 2027', short: 'National Bank Open', season: '2027', kind: 'slam',
    window: { from: '2027-07-31', until: '2027-08-16' }, venue: 'IGA Stadium', city: 'Montreal', tz: 'America/Toronto', pick: pickTennis(/\bnational bank open\b/i), whole: ['national bank open', 'rogers cup', 'canadian open tennis'],
    facts: {
      format: "Numbered sessions with the round in the marketplace's name, from qualifying onward.",
      final: 'Last date listed: {last}',
      sources: [WIKI('National Bank Open', 'National_Bank_Open')],
    },
  }),
  comp({
    id: 'wta-finals-2026', tag: 'Tennis', name: 'WTA Finals 2026', short: 'WTA Finals', season: '2026', kind: 'slam',
    window: { from: '2026-11-08', until: '2026-11-15' }, venue: 'Indian Wells Tennis Garden', city: 'Indian Wells', tz: 'America/Los_Angeles', pick: pickTennis(/\bwta finals\b/i), whole: ['wta finals'],
    facts: {
      format: 'Numbered sessions, one a day in our listings; the order of play is set by the tour.',
      final: 'Last date listed: {last}',
      sources: [WIKI('2026 WTA Finals', '2026_WTA_Finals')],
    },
  }),
  comp({
    id: 'fight-cards-2026-27', tag: 'UFC and boxing', name: 'UFC and Boxing Fight Cards 2026-27', short: 'Fight cards', season: '2026-27', kind: 'fight-card',
    window: { from: '2026-10-23', until: '2027-12-31' }, venue: 'Arenas and casinos across the United States and abroad', pick: pickFights,
    whole: ['ufc', 'ufc fight night', /^ufc \d{2,3}$/, 'boxing', 'boxing fights', 'mma', 'fight night', 'bkfc', 'fight cards'],
    facts: {
      format: "Cards are listed with their headline fight where the marketplace names it. A card number (UFC 334) is the promotion's own; weekly series and amateur tournaments are left out.",
      final: 'Last card listed: {last}',
      sources: [WIKI('List of UFC events', 'List_of_UFC_events'), WIKI('Bare Knuckle Fighting Championship', 'Bare_Knuckle_Fighting_Championship')],
    },
  }),
  comp({
    id: 'six-nations-2027', tag: 'Rugby', name: 'Six Nations 2027', short: 'Six Nations', season: '2027', kind: 'round-robin',
    window: { from: '2027-02-05', until: '2027-03-13' }, venue: 'Stadiums in England, Scotland, Wales, Ireland, France and Italy', tz: 'Europe/London',
    pick: pickSixNations, whole: ['six nations', '6 nations', 'six nations rugby', 'rugby six nations', 'guinness six nations', 'six nations tournament'],
    facts: {
      format: 'Six nations play each other once: 15 matches in five rounds. Every nation plays two or three of its five matches at home.',
      final: 'Last match listed: {last}',
      sources: [WIKI('2027 Six Nations Championship', '2027_Six_Nations_Championship')],
    },
  }),

  // ---- wave 3b (owner 2026-10-23): the competitions the legal switches held out, back now that every switch is OFF ----
  // Window dates of the tennis hubs are the span of the partners' listings (read 2026-10-23), not the tournament's own announced dates;
  // only venue, city and (ATP Finals) dates are from the sources named. Serie A / Ligue 1 clubs and season span: Wikipedia, read 2026-10-23.
  comp({
    id: 'atp-finals-2026', tag: 'Tennis', name: 'Nitto ATP Finals 2026', short: 'ATP Finals', season: '2026', kind: 'slam', courts: true,
    window: { from: '2026-11-15', until: '2026-11-22' }, venue: 'Inalpi Arena', city: 'Turin', tz: 'Europe/Rome', pick: pickTennis(/\b(?:nitto )?atp finals\b/i), whole: ['atp finals', 'nitto atp finals', 'atp finals turin', 'atp tour finals'],
    facts: {
      format: 'Afternoon and evening sessions at the Inalpi Arena in Turin, 15 to 22 November 2026; the order of play is set by the tour.',
      final: 'Last date listed: {last}',
      sources: [WIKI('2026 ATP Finals', '2026_ATP_Finals')],
    },
  }),
  comp({
    id: 'australian-open-2027', tag: 'Tennis', name: 'Australian Open 2027', short: 'Australian Open', season: '2027', kind: 'slam', courts: true,
    window: { from: '2027-01-11', until: '2027-01-31' }, venue: 'Melbourne Park', city: 'Melbourne', tz: 'Australia/Melbourne', pick: pickTennis(/\baustralian open\b(?!\s+golf)/i), whole: ['australian open', 'australian open tennis', 'aus open'],
    facts: {
      format: 'Day and night sessions on the show courts (Rod Laver Arena, John Cain Arena, Margaret Court Arena) and grounds passes, as the marketplaces list them; the order of play is set by the tournament.',
      final: 'Last date listed: {last}',
      sources: [WIKI('Australian Open', 'Australian_Open')],
    },
  }),
  comp({
    id: 'roland-garros-2027', tag: 'Tennis', name: 'Roland-Garros 2027', short: 'Roland-Garros', season: '2027', kind: 'slam', courts: true,
    window: { from: '2027-05-23', until: '2027-06-06' }, venue: 'Stade Roland-Garros', city: 'Paris', tz: 'Europe/Paris', pick: pickTennis(/\b(?:roland[- ]?garros|french open)\b(?!\s+golf)/i), whole: ['roland garros', 'french open', 'french open tennis', 'roland garros tennis'],
    facts: {
      format: 'Sessions with the round in the marketplace name (the night sessions on Court Philippe-Chatrier are listed separately); the order of play is set by the tournament.',
      final: 'Last date listed: {last}',
      sources: [WIKI('French Open', 'French_Open')],
    },
  }),
  comp({
    id: 'wimbledon-2027', tag: 'Tennis', name: 'Wimbledon 2027', short: 'Wimbledon', season: '2027', kind: 'slam', courts: true,
    window: { from: '2027-06-28', until: '2027-07-11' }, venue: 'All England Lawn Tennis and Croquet Club', city: 'London', tz: 'Europe/London', pick: pickTennis(/\bwimbledon\b(?!\s+(?:theatre|theater|park|common|village|greyhound|dons))/i), whole: ['wimbledon', 'wimbledon tennis', 'wimbledon championships', 'the championships wimbledon'],
    facts: {
      format: 'Numbered sessions on Centre Court and No. 1 Court, as the marketplaces list them; the order of play is set by the tournament.',
      final: 'Last date listed: {last}',
      sources: [WIKI('The Championships, Wimbledon', 'Wimbledon_Championships')],
    },
  }),
  comp({
    id: 'italian-open-2027', tag: 'Tennis', name: 'Italian Open (Rome) 2027', short: 'Italian Open', season: '2027', kind: 'slam', courts: true,
    window: { from: '2027-05-05', until: '2027-05-16' }, venue: 'Foro Italico', city: 'Rome', tz: 'Europe/Rome', pick: pickTennis(/\b(?:internazionali|italian open|rome masters)\b/i), whole: ['italian open', 'italian open tennis', 'internazionali', 'internazionali bnl d italia', 'internazionali d italia', 'rome masters'],
    facts: {
      format: 'Morning and evening sessions at the Foro Italico, as the marketplaces list them; the order of play is set by the tournament.',
      final: 'Last date listed: {last}',
      sources: [WIKI('Italian Open (tennis)', 'Italian_Open_%28tennis%29')],
    },
  }),
  comp({
    id: 'madrid-open-2027', tag: 'Tennis', name: 'Madrid Open 2027', short: 'Madrid Open', season: '2027', kind: 'slam', courts: true,
    window: { from: '2027-04-19', until: '2027-05-02' }, venue: 'La Caja Mágica', city: 'Madrid', tz: 'Europe/Madrid', pick: pickTennis(/\bmadrid open\b/i), whole: ['madrid open', 'mutua madrid open', 'madrid open tennis'],
    facts: {
      format: 'Daytime and evening sessions at the Caja Mágica, as the marketplaces list them; the order of play is set by the tournament.',
      final: 'Last date listed: {last}',
      sources: [WIKI('Madrid Open (tennis)', 'Madrid_Open_%28tennis%29')],
    },
  }),
  comp({
    id: 'miami-open-2027', tag: 'Tennis', name: 'Miami Open 2027', short: 'Miami Open', season: '2027', kind: 'slam', courts: true,
    window: { from: '2027-03-14', until: '2027-03-28' }, venue: 'Hard Rock Stadium', city: 'Miami Gardens', tz: 'America/New_York', pick: pickTennis(/\bmiami open\b/i), whole: ['miami open', 'miami open tennis'],
    facts: {
      format: 'Numbered sessions in the stadium and on the Grandstand, as the marketplaces list them; the order of play is set by the tournament.',
      final: 'Last date listed: {last}',
      sources: [WIKI('Miami Open (tennis)', 'Miami_Open_%28tennis%29')],
    },
  }),
  comp({
    id: 'indian-wells-2027', tag: 'Tennis', name: 'BNP Paribas Open (Indian Wells) 2027', short: 'Indian Wells', season: '2027', kind: 'slam', courts: true,
    window: { from: '2027-03-03', until: '2027-03-14' }, venue: 'Indian Wells Tennis Garden', city: 'Indian Wells', tz: 'America/Los_Angeles', pick: pickTennis(/\bbnp paribas open\b/i), whole: ['bnp paribas open', 'indian wells masters', 'indian wells tennis', 'bnp paribas open indian wells'],
    facts: {
      format: 'Numbered sessions in Stadium 1 and the other show courts, as the marketplaces list them; the order of play is set by the tournament.',
      final: 'Last date listed: {last}',
      sources: [WIKI('Indian Wells Masters', 'Indian_Wells_Masters')],
    },
  }),
  comp({
    id: 'monte-carlo-masters-2027', tag: 'Tennis', name: 'Monte-Carlo Masters 2027', short: 'Monte-Carlo Masters', season: '2027', kind: 'slam',
    window: { from: '2027-04-03', until: '2027-04-11' }, venue: 'Monte-Carlo Country Club', city: '', tz: 'Europe/Monaco', pick: pickTennis(/\bmonte[- ]carlo masters\b/i), whole: ['monte carlo masters', 'rolex monte carlo masters', 'monte carlo tennis'],
    facts: {
      format: 'Marketplaces sell this tournament by day, named by round (qualifying to the finals); the order of play is set by the tournament.',
      final: 'Last date listed: {last}',
      sources: [WIKI('Monte-Carlo Masters', 'Monte-Carlo_Masters')],
    },
  }),
  {
    id: 'serie-a-2026-27',
    tag: 'Soccer',
    name: 'Serie A 2026/27',
    short: 'Serie A',
    season: '2026/27',
    kind: 'league',
    window: { from: '2026-08-01', until: '2027-05-30' },
    venue: 'Clubs across Italy',
    city: '',
    tz: 'Europe/Rome',
    aliases: ['serie a', 'italian serie a', 'serie a tickets', 'serie a football', 'italian league'],
    queryNot: /\b(?:serie b|serie c|serie a2|brazil|brazilian|brasil|brasileirao|women|womens|women s|femminile|basket|basketball|volley|volleyball|netflix|funding|startup|round)\b/,
    memberTest: null,
    league: {
      country: 'IT',
      kinds: ['soccer'],
      tournamentOk: /^serie a$/i,
      notGame: /friendly|testimonial|women|femminile|primavera/i,
      floatDates: true,
      entry: 'Clubs sell personal tickets; a resold ticket can be refused at the gate.',
      clubs: [
        'Atalanta', 'Bologna', 'Cagliari', 'Como', 'Fiorentina|ACF Fiorentina', 'Frosinone', 'Genoa', 'Inter Milan|Inter|Internazionale', 'Juventus', 'Lazio', 'Lecce', 'AC Milan|Milan',
        'Monza', 'Napoli', 'Parma', 'Roma', 'Sassuolo', 'Torino', 'Udinese', 'Venezia',
      ],
    },
    facts: {
      format: '20 clubs; each plays the others once at home and once away, 38 games in all. The league sets dates and kick-off times in batches, so a game without a kick-off time can still move.',
      prize: null,
      defending: null,
      final: 'Last game in our listings: {last}',
      faceValue: null,
      official: null,
      sources: [{ name: 'Wikipedia: 2026–27 Serie A', url: 'https://en.wikipedia.org/wiki/2026%E2%80%9327_Serie_A', licence: 'CC BY-SA 4.0' }],
    },
  },
  {
    id: 'ligue-1-2026-27',
    tag: 'Soccer',
    name: 'Ligue 1 2026/27',
    short: 'Ligue 1',
    season: '2026/27',
    kind: 'league',
    window: { from: '2026-08-01', until: '2027-05-29' },
    venue: 'Clubs across France',
    city: '',
    tz: 'Europe/Paris',
    aliases: ['ligue 1', 'ligue1', 'french ligue 1', 'ligue 1 tickets', 'french league', 'french football league'],
    queryNot: /\b(?:ligue 2|ligue 1 2|feminine|women|womens|women s|basket|handball|volley)\b/,
    memberTest: null,
    league: {
      country: 'FR',
      kinds: ['soccer'],
      tournamentOk: /^ligue 1$/i,
      notGame: /friendly|testimonial|women|feminin/i,
      floatDates: true,
      entry: 'Clubs sell personal tickets; a resold ticket can be refused at the gate.',
      clubs: [
        'Angers|Angers SCO', 'Auxerre|AJ Auxerre', 'Brest|Stade Brestois 29', 'Le Havre|Le Havre AC', 'Le Mans|Le Mans FC', 'Lens|RC Lens', 'Lille|Lille OSC|LOSC Lille', 'Lorient|FC Lorient',
        'Lyon|Olympique Lyonnais', 'Marseille|Olympique de Marseille|Olympique Marseille', 'Monaco|AS Monaco', 'Nice|OGC Nice', 'Paris FC', 'Paris Saint-Germain|PSG|Paris SG',
        'Rennes|Stade Rennais|Stade de Rennes', 'Strasbourg|RC Strasbourg', 'Toulouse|Toulouse FC', 'Troyes|Troyes AC',
      ],
    },
    facts: {
      format: '18 clubs; each plays the others once at home and once away, 34 games in all. Dates and kick-off times follow the broadcasters, so a game without a kick-off time can still move.',
      prize: null,
      defending: null,
      final: 'Last game in our listings: {last}',
      faceValue: null,
      official: null,
      sources: [{ name: 'Wikipedia: 2026–27 Ligue 1', url: 'https://en.wikipedia.org/wiki/2026%E2%80%9327_Ligue_1', licence: 'CC BY-SA 4.0' }],
    },
  },
];

// ---- runs: tours, residencies and London shows (PLAN W3-5 / W3-6) ---------------------------------------------
// An act's dates come from the partners' own pages (scripts/gen-tournaments.mjs buildRun); only the NAME of what
// belongs together is hand-entered, nothing else, so nothing here can be a wrong fact. Counts and ranges are
// whatever the build finds. No official route, face value or box-office link anywhere (owner rule, 2026-10-22:
// visitors go to our partners only).

/** An act's name without case, accents or punctuation: "Les Misérables", "Les Miserables" and "LES MISERABLES!" meet. @param {string} s */
export const normName = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '');

/**
 * Is this event page a date of the run? `row`: { artist?, name?, venue?, city?, category? } (scripts/gen-longtail.mjs writes them).
 * @param {RunSpec} run @param {{ artist?: string | null, name?: string | null, venue?: string | null, city?: string | null, category?: string | null }} row
 */
export const runMember = (run, row) => {
  const artist = String(row.artist ?? '');
  if (!artist) return false;
  if (run.artistIs ? !run.artistIs.some((a) => normName(a) === normName(artist)) : !run.artist?.test(artist)) return false;
  if (run.category && row.category && row.category !== run.category) return false;
  if (run.nameNot?.test(String(row.name ?? ''))) return false;
  if (run.venue && !run.venue.test(String(row.venue ?? ''))) return false;
  if (run.city && !run.city.test(String(row.city ?? ''))) return false;
  return true;
};

/** Does this event page belong to the tournament or run? One answer for the build's two scripts.
 *  @param {Tournament} t @param {{ tournament?: string | null, name?: string | null, artist?: string | null, venue?: string | null, city?: string | null, category?: string | null }} row */
export const isMember = (t, row) =>
  t.run ? runMember(t.run, row) : t.kind === 'league' ? !!leagueGame(t, row) : !!t.memberTest?.test(`${row.tournament ?? ''} ${row.name ?? ''}`);

/** @param {string} id @param {string} name @param {string} short @param {RunSpec} run @param {Partial<Tournament> & { aliases: string[] }} o @returns {Tournament} */
const runEntry = (id, name, short, run, o) => ({
  id, name, short, season: '', kind: 'run', tag: run.sub === 'show' ? 'Theatre' : run.sub === 'residency' ? 'Residency' : 'Tour',
  window: { from: '2026-10-23', until: '2027-12-31' }, venue: 'Various venues', city: '', tz: 'UTC', memberTest: null, run,
  facts: { format: '', prize: null, defending: null, final: '', faceValue: null, official: null, sources: [] },
  ...o,
});

// Tours and residencies. Windows are the dates the catalog held on 2026-10-23 (the search card stops showing after `until`).
// Olivia Rodrigo: Milan lists "Olivia Rodrigo & Die Spitz" as the act; the drag brunch and tribute nights are other products.
// Metallica and the Eagles: the Sphere dates only (the "2 Day Pass" rows are a second product on the same night).
/** @type {Tournament[]} */
const RUNS = [
  runEntry('olivia-rodrigo-tour-2026-27', 'Olivia Rodrigo tour dates 2026-27', 'Olivia Rodrigo',
    { sub: 'tour', artist: /^olivia rodrigo(?: & .+)?$/i, nameNot: /drag brunch|\bnight\b|kids|tribute|playhouse/i, perDay: true, minPriced: 3 },
    { window: { from: '2026-10-27', until: '2027-05-09' }, aliases: ['olivia rodrigo'], notWith: /tribute|night|kids|brunch|drag|playhouse/ }),
  runEntry('harry-styles-tour-2026-27', 'Harry Styles tour dates 2026-27', 'Harry Styles',
    { sub: 'tour', artist: /^harry styles$/i, perDay: true, minPriced: 3 },
    { window: { from: '2026-10-23', until: '2027-08-28' }, aliases: ['harry styles'], notWith: /tribute|night|kids|brunch/ }),
  runEntry('oasis-live-2027', 'Oasis Live 2027 tour dates', 'Oasis',
    { sub: 'tour', artist: /^oasis$/i, perDay: true, minPriced: 3 },
    { window: { from: '2027-05-21', until: '2027-09-26' }, aliases: ['oasis'], notWith: /ministry|church|tribute|hotel|resort|cafe|restaurant|spa|dome|center|centre|casino/ }),
  runEntry('metallica-at-sphere', 'Metallica at Sphere, Las Vegas', 'Metallica at Sphere',
    { sub: 'residency', artist: /^metallica$/i, venue: /\bsphere\b/i, nameNot: /day pass/i, perDay: true, minPriced: 3 },
    { window: { from: '2026-10-24', until: '2027-03-13' }, venue: 'Sphere', city: 'Las Vegas', aliases: ['metallica sphere', 'metallica at sphere', 'metallica live at sphere', 'metallica las vegas'], notWith: /tribute/ }),
  runEntry('eagles-at-sphere', 'Eagles at Sphere, Las Vegas', 'Eagles at Sphere',
    { sub: 'residency', artist: /^eagles$/i, venue: /\bsphere\b/i, perDay: true, minPriced: 3 },
    // "eagles" alone is Philadelphia: only phrases that name the band at Sphere
    { window: { from: '2026-11-13', until: '2027-01-23' }, venue: 'Sphere', city: 'Las Vegas', aliases: ['eagles sphere', 'eagles at sphere', 'eagles live at sphere', 'eagles band sphere'] }),
  runEntry('bts-tour', 'BTS tour dates', 'BTS',
    { sub: 'tour', artist: /^bts$/i, perDay: true, minPriced: 3 },
    { window: { from: '2026-10-23', until: '2027-02-21' }, aliases: ['bts'], notWith: /tribute|bt21/ }),
];

/**
 * London shows (PLAN W3-6): [slug, page name, the act's spellings in the partners' feeds, options]. The list is the shows
 * the catalog held with 10+ priced performances at an ordinary London theatre on 2026-10-23: no dinner shows, ice shows,
 * Royal Opera House, Coliseum opera or ballet (not shows in the West End sense), nothing ending within a week.
 * `amb`: the name alone is a common word ("Pride", "Rent"), so no "{name} london" search phrase, only "{name} west end".
 * `alt`: other names people type ("six", "phantom").
 * @type {[string, string, string[], { amb?: boolean, alt?: string[] }?][]}
 */
export const WEST_END = [
  ['six', 'Six the Musical', ['Six The Musical', 'Six'], { alt: ['six'] }],
  ['paddington', 'Paddington the Musical', ['Paddington - The Musical', 'Paddington The Musical']],
  ['mamma-mia', 'Mamma Mia!', ['Mamma Mia!']],
  ['phantom-of-the-opera', 'The Phantom of the Opera', ['The Phantom Of The Opera'], { alt: ['phantom of the opera'] }],
  ['les-miserables', 'Les Misérables', ['Les Miserables'], { alt: ['les mis'] }],
  ['devil-wears-prada', 'The Devil Wears Prada the Musical', ['The Devil Wears Prada - Musical'], { alt: ['devil wears prada'] }],
  ['hamilton', 'Hamilton', ['Hamilton']],
  ['witness-for-the-prosecution', 'Witness for the Prosecution', ['Witness For The Prosecution']],
  ['cabaret', 'Cabaret', ['Cabaret'], { amb: true }],
  ['the-mousetrap', 'The Mousetrap', ['The Mousetrap'], { alt: ['mousetrap'] }],
  ['matilda', 'Matilda the Musical', ['Matilda - The Musical', 'Matilda The Musical'], { alt: ['matilda'] }],
  ['the-lion-king', 'The Lion King', ['The Lion King'], { alt: ['lion king'] }],
  ['hadestown', 'Hadestown', ['Hadestown']],
  ['harry-potter-cursed-child', 'Harry Potter and the Cursed Child', ['Harry Potter and The Cursed Child'], { alt: ['cursed child'] }],
  ['miss-saigon', 'Miss Saigon', ['Miss Saigon']],
  ['wicked', 'Wicked', ['Wicked']],
  ['rent', 'Rent', ['Rent'], { amb: true }],
  ['beetlejuice', 'Beetlejuice the Musical', ['Beetlejuice - The Musical'], { alt: ['beetlejuice'] }],
  ['moulin-rouge', 'Moulin Rouge! The Musical', ['Moulin Rouge - The Musical'], { alt: ['moulin rouge'] }],
  ['pride', 'Pride', ['Pride'], { amb: true }],
  ['billy-elliot', 'Billy Elliot the Musical', ['Billy Elliot'], { alt: ['billy elliot'] }],
  ['sinatra', 'Sinatra the Musical', ['Sinatra The Musical']],
  ['oliver', 'Oliver!', ['Oliver!'], { amb: true }],
  ['the-book-of-mormon', 'The Book of Mormon', ['The Book Of Mormon'], { alt: ['book of mormon'] }],
  ['paranormal-activity', 'Paranormal Activity', ['Paranormal Activity - Play']],
  ['one-day', 'One Day the Musical', ['One Day - The Musical'], { amb: true }],
  ['the-play-that-goes-wrong', 'The Play That Goes Wrong', ['The Play That Goes Wrong'], { alt: ['play that goes wrong'] }],
  ['oh-mary', 'Oh, Mary!', ['Oh Mary!', 'Oh, Mary!']],
  ['the-popinjay-cavalier', 'The Popinjay Cavalier', ['The Popinjay Cavalier']],
  ['amadeus', 'Amadeus', ['Amadeus'], { amb: true }],
  ['my-neighbour-totoro', 'My Neighbour Totoro', ['My Neighbour Totoro'], { alt: ['totoro'] }],
  ['into-the-woods', 'Into the Woods', ['Into The Woods']],
  ['ivanov', 'Ivanov', ['Ivanov'], { amb: true }],
  ['jesus-christ-superstar', 'Jesus Christ Superstar', ['Jesus Christ Superstar']],
  ['charlie-and-the-chocolate-factory', 'Charlie and the Chocolate Factory', ['Charlie and The Chocolate Factory']],
  ['avenue-q', 'Avenue Q', ['Avenue Q']],
  ['titanique', 'Titanique', ['Titanique - Musical', 'Titanique']],
  ['the-traitors-acts-of-betrayal', 'The Traitors: Acts of Betrayal', ['The Traitors - Acts of Betrayal'], { alt: ['traitors acts of betrayal'] }],
  ['the-cherry-orchard', 'The Cherry Orchard', ['The Cherry Orchard']],
  ['stranger-things-the-first-shadow', 'Stranger Things: The First Shadow', ['Stranger Things: The First Shadow'], { alt: ['stranger things first shadow'] }],
  ['legally-blonde', 'Legally Blonde', ['Legally Blonde']],
  ['the-seagull', 'The Seagull', ['The Seagull'], { amb: true }],
  ['the-lives-of-others', 'The Lives of Others', ['The Lives of Others']],
  ['a-christmas-carol-old-vic', 'A Christmas Carol at the Old Vic', ['A Christmas Carol'], { amb: true }],
  ['death-note', 'Death Note the Musical', ['Death Note'], { amb: true }],
  ['hay-fever', 'Hay Fever', ['Hay Fever'], { amb: true }],
  ['the-snowman', 'The Snowman', ['The Snowman'], { amb: true }],
  ['julius-caesar', 'Julius Caesar', ['Julius Caesar'], { amb: true }],
  ['stick-man', 'Stick Man', ['Stick Man'], { amb: true }],
  ['matthew-bournes-cinderella', "Matthew Bourne's Cinderella", ["Matthew Bourne's Cinderella"], { alt: ['matthew bourne cinderella'] }],
  ['peppa-pigs-big-family-show', "Peppa Pig's Big Family Show!", ["Peppa Pig's Big Family Show!"], { alt: ['peppa pig'] }],
  ['little-dancer', 'Little Dancer', ['Little Dancer'], { amb: true }],
  ['christmas-carol-goes-wrong', 'Christmas Carol Goes Wrong', ['Christmas Carol Goes Wrong']],
  ['ilford-boy', 'Ilford Boy', ['Ilford Boy'], { amb: true }],
  ['room-on-the-broom', 'Room on the Broom', ['Room On The Broom'], { amb: true }],
  ['pinocchio', 'Pinocchio', ['Pinocchio'], { amb: true }],
  ['the-smartest-giant-in-town', 'The Smartest Giant in Town', ['The Smartest Giant In Town'], { amb: true }],
  ['king-john', 'King John', ['King John - Play', 'King John'], { amb: true }],
  ['the-animals-come-to-the-table', 'The Animals Come to the Table', ['The Animals Come To The Table'], { amb: true }],
  ['potted-panto', 'Potted Panto', ['Potted Panto']],
  ['the-choir-of-man', 'The Choir of Man', ['The Choir of Man'], { alt: ['choir of man'] }],
  ['dirty-dancing', 'Dirty Dancing', ['Dirty Dancing']],
];

/** The West End shows that are musicals: "hamilton musical", "wicked the musical" name the show (a play has no such phrase: "the mousetrap musical" is nobody's search). */
const MUSICALS = new Set(['six', 'paddington', 'mamma-mia', 'phantom-of-the-opera', 'les-miserables', 'devil-wears-prada', 'hamilton', 'matilda', 'the-lion-king', 'hadestown', 'miss-saigon', 'wicked', 'rent', 'beetlejuice', 'moulin-rouge', 'billy-elliot', 'sinatra', 'oliver', 'the-book-of-mormon', 'one-day', 'jesus-christ-superstar', 'charlie-and-the-chocolate-factory', 'avenue-q', 'titanique', 'into-the-woods', 'legally-blonde', 'death-note', 'cabaret', 'dirty-dancing']);
for (const [slug, name, artists, o = {}] of WEST_END) {
  const w = (s) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
  const names = [name, ...(o.alt ?? [])].map(w);
  TOURNAMENTS.push(runEntry(`west-end-${slug}`, `${name} in London`, name,
    { sub: 'show', category: 'Theater', artistIs: artists, city: /^london$/i, perDay: false, minPriced: 10 },
    // a "{name} london" phrase for a name that is not a common word; "west end" is enough of a signal for the rest
    { window: { from: '2026-10-23', until: '2028-03-31' }, tz: 'Europe/London', city: 'London', venue: 'London', aliases: [...new Set(names.flatMap((n) => [...(o.amb ? [] : [`${n} london`, `${n} show`, `${n} theatre`, `${n} theater`]), ...(MUSICALS.has(slug) ? [`${n.replace(/ (the )?musical$/, '')} musical`, `${n.replace(/ (the )?musical$/, '')} the musical`] : []), `${n} west end`]))] }));
}
TOURNAMENTS.push(...RUNS);

export const tournamentById = (id) => TOURNAMENTS.find((t) => t.id === id) ?? null;
export const tournamentPath = (id) => `/tournament/${id}`;

/**
 * Phrases a SEARCH reads as a season hub although no game's title says them: "nba finals" and "stanley cup final" are the NBA and NHL
 * seasons' hubs (their page says the Finals / playoffs follow and that a game which may not be played is marked). They are NOT aliases:
 * tournamentForEvent reads `aliases` to decide which games belong to a tournament, and a listing titled after a round that has not been
 * played must not be pulled into a season (src/speculative.ts keeps the placeholders out). Search-only, so no event is invented.
 * Like `whole`, they name the tournament only when the query is nothing else ("nba finals", "nba finals tickets 2027"; not "nba finals lakers").
 * @type {Record<string, string[]>}
 */
const SEARCH_ONLY = {
  'nba-2026-27': ['nba finals', 'nba final', 'nba playoffs', 'nba playoff', 'nba championship'],
  'nhl-2026-27': ['stanley cup', 'stanley cup final', 'stanley cup finals', 'stanley cup playoffs', 'nhl playoffs', 'nhl finals'],
};
/** The phrases a search reads as this tournament: its aliases and the search-only ones. @param {Tournament} t */
const searchPhrases = (t) => (SEARCH_ONLY[t.id] ? [...t.aliases, ...SEARCH_ONLY[t.id]] : t.aliases);

/** The registry entry a free-text query names, or null (aliases are whole phrases). The LONGEST matching alias wins
 *  ("premier league darts" is the darts, plain "premier league" the football); an entry's `queryNot` vetoes the query
 *  ("la liga 2", "premier league snooker") and its `notWith` words rule it out. @param {string} q */
/** A `notWith` list matches WORDS (and their plurals), never pieces of words: "night" is not in "tonight", "dome" not in "Dominic".
 *  @type {WeakMap<RegExp, RegExp>} */
const WORDS = new WeakMap();
/** @param {RegExp} re */
const wholeWords = (re) => WORDS.get(re) ?? WORDS.set(re, new RegExp(`(?<![a-z0-9])(?:${re.source})(?:e?s)?(?![a-z0-9])`, re.flags.replace('g', ''))).get(re);
export const tournamentForQuery = (q) => {
  // accents folded first: "Les Misérables" is "les miserables", not "les mis rables"
  const plain = String(q ?? '').normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const s = ` ${plain} `;
  /** @type {Tournament | null} */
  let best = null;
  let bestLen = 0;
  for (const t of TOURNAMENTS) {
    if (t.queryNot?.test(plain) || (t.notWith && wholeWords(t.notWith).test(s))) continue;
    for (const x of t.aliases) if (x.length > bestLen && s.includes(` ${x} `)) [best, bestLen] = [t, x.length];
  }
  if (best) return best;
  // `whole` (the wave-3 hubs): the query is the name and nothing else ("nba tickets 2026-27"), so "lakers nba" and "f1 monaco"
  // keep their own answers; an alias match above (a longer, more specific phrase) always comes first
  const bare = plain.split(' ').filter((w) => w && !WHOLE_NOISE.has(w)).join(' ');
  if (!bare) return null;
  // (the search-only phrases are whole-query phrases too: "nba finals" is the NBA season, "nba finals lakers" keeps its own answer)
  return TOURNAMENTS.find((t) => (t.whole ?? []).some((w) => (w instanceof RegExp ? w.test(bare) : w === bare)) || (SEARCH_ONLY[t.id] ?? []).includes(bare)) ?? null;
};
const WHOLE_NOISE = new Set(['tickets', 'ticket', 'tix', 'game', 'games', 'schedule', 'season', '2026', '2027', '26', '27', 'the']);

/**
 * The competition a half-typed query is the start of (type-ahead only; a submitted query uses tournamentForQuery): "eredivi" is the
 * Eredivisie, "premier le" the Premier League, "nba fin" the NBA season (search-only phrases too).
 * The rules:
 *   - 4 letters or more in all, at most 4 words; the words before the last one are whole words of the phrase from its START ("premier le"),
 *     and the last is a STRICT prefix of the phrase's word at that place. A phrase typed whole is tournamentForQuery's ("premier league").
 *   - only a competition with a card (a tag), not a tour or a show (kind 'run'), not one whose window is over on `today` (when given).
 *   - the tournament must be UNIQUE: two competitions answering is no answer ("prem" is the Premier League AND the Premier League Darts → none).
 *     With two words or more typed, the shorter phrase wins over the longer one of the other that extends it, as the LONGEST-alias rule has
 *     it for the whole phrase: "premier le" is the football league, "premier league dar" the darts (only the darts phrase goes that far).
 *   - a tournament's own `queryNot` / `notWith` veto applies to the words as typed.
 *   - `minLetters` (default 4): the search box asks for 6, because the stems of 4 and 5 letters are names and places first ("alex" is not
 *     Alexandra Palace, "josh" not Fury-Joshua, "madr" not the Madrid Open, "miam" not the Miami Open, "aust" not the Australian Open).
 * @param {string} q @param {string | null} [today] "YYYY-MM-DD" @param {{ minLetters?: number }} [opts]
 * @returns {Tournament | null}
 */
export const tournamentForPrefix = (q, today = null, { minLetters = 4 } = {}) => {
  const plain = String(q ?? '').normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const typed = plain.split(' ').filter(Boolean);
  if (!typed.length || typed.length > 4 || typed.join('').length < minLetters) return null;
  const last = typed[typed.length - 1];
  const s = ` ${plain} `;
  /** @type {Map<Tournament, string[]>} */
  const hits = new Map();
  for (const t of TOURNAMENTS) {
    if (!t.tag || t.run || (today && today > t.window.until)) continue;
    if (t.queryNot?.test(plain) || (t.notWith && wholeWords(t.notWith).test(s))) continue;
    const name = norm(t.short ?? '');
    for (const phrase of [name, ...searchPhrases(t).map(norm)]) {
      const words = phrase.split(' ');
      if (words.length < typed.length || !typed.slice(0, -1).every((w, i) => words[i] === w)) continue;
      const word = words[typed.length - 1];
      if (word.length > last.length && word.startsWith(last)) hits.set(t, [...(hits.get(t) ?? []), phrase]);
    }
  }
  if (hits.size <= 1) return [...hits.keys()][0] ?? null;
  // two words or more typed: the competition whose shortest phrase is the start of every phrase of the others
  if (typed.length < 2) return null;
  const winners = [...hits].filter(([t, phrases]) => {
    const shortest = phrases.reduce((a, b) => (b.length < a.length ? b : a));
    return [...hits].every(([u, others]) => u === t || others.every((x) => x.startsWith(`${shortest} `)));
  });
  return winners.length === 1 ? winners[0][0] : null;
};

const norm = (q) => String(q ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
// words that may sit around a name without changing who is meant ("luke littler tickets 2027")
const NAME_FILLER = new Set(['tickets', 'ticket', 'tix', 'darts', 'tour', 'live', '2026', '2027']);

/** How many days ahead a tournament still counts for a searched player (see tournamentForMember). */
export const PLAYER_LOOKAHEAD_DAYS = 90;

/**
 * The tournament a searched PLAYER is in (PLAN W2-3): the whole query is the player's name or distinctive
 * surname (plus filler words like "tickets"), and the tournament is ahead or running with its first game
 * within PLAYER_LOOKAHEAD_DAYS (or already started). Never a substring match: "littler" inside a longer query that names
 * something else is left alone. Tournaments without a `players` list never match. The caller (functions/api/search.ts) still
 * shows the card only for a tournament whose page was built, i.e. one with live priced events.
 * 90 days, not 30 (review 2026-10-30): the World Darts Championship is on sale from the summer, and "luke littler" / "van gerwen"
 * found nothing 49 days out although the tournament page was live and priced.
 * @param {string} q @param {string} today "YYYY-MM-DD"
 */
export const tournamentForMember = (q, today) => {
  const words = norm(q).split(' ').filter((w) => w && !NAME_FILLER.has(w));
  if (!words.length) return null;
  const s = words.join(' ');
  return (
    TOURNAMENTS.find((t) => {
      if (!t.players || !t.tag || today > t.window.until || dayAdd(today, PLAYER_LOOKAHEAD_DAYS) < t.window.from) return false;
      return t.players.some((p) => norm(p.name) === s || (p.short && p.short === s));
    }) ?? null
  );
};

/**
 * The registry tournament an event row belongs to, or null: the row's competition name or title names it
 * (memberTest, or the aliases for a tournament with no test) AND its date falls inside the tournament's
 * window. Pure and cheap: the event page calls it in the prerender, the edge render and the client, so all
 * three print the same "Part of" line (PLAN W2-4).
 * @param {{ name?: string, tournament?: string, date?: string } | null | undefined} ev
 */
// a party, tailgate or screening named after a game is not a game of the tournament
const SIDE_EVENT = /\b(?:party|parties|afterparty|after party|tailgate|watch|viewing|screening|experience|brunch|pregame)\b/i;

export const tournamentForEvent = (ev) => {
  if (!ev?.date || SIDE_EVENT.test(ev.name ?? '')) return null;
  const hay = `${ev.tournament ?? ''} ${ev.name ?? ''}`;
  const s = ` ${hay.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()} `;
  return (
    TOURNAMENTS.find(
      (t) => {
        const day = ev.date.slice(0, 10);
        if (t.run) return false; // a tour or show is found by its act (runMember), not by the event's title: no "Part of" line
        if (day < t.window.from || day > t.window.until) return false;
        if (t.memberTest) return t.memberTest.test(hay);
        // The World Series has no name test: "World Series of Poker", "Little League World Series" and
        // "World Series of Darts" carry the alias too, so only a game the postseason reader takes for an
        // MLB World Series game belongs (shared/postseason-search.mjs, as search reads them).
        if (t.id.startsWith('world-series-')) return postseasonGame({ name: ev.name ?? '' })?.round === 'World Series';
        return t.aliases.some((a) => s.includes(` ${a} `));
      }
    ) ?? null
  );
};

// ---- leagues (kind 'league'): which game belongs to a competition, who is home ------------------------------------
/** Words that only dress a club's name; numbers go too ("Bayer 04 Leverkusen" = "Bayer Leverkusen"). */
const CLUB_DRESS = new Set('fc afc cf cfc sc ac as ssc ss us rc rcd cd ud sd ad ca bc sk fk jk vfl vfb sv tsv bv fsv tsg sbv de du di del la le les olympique stade sporting calcio club'.split(' '));
/** One key per club, whatever the partner's spelling: "FC Köln" and "1. FC Koln" are `koln`. @param {string} s */
export const clubId = (s) =>
  String(s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, ' ')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .split(/\s+/)
    .filter((w) => w && !CLUB_DRESS.has(w) && !/^\d+$/.test(w))
    .join(' ');

/** @type {WeakMap<object, Map<string, string>>} */
const BOOKS = new WeakMap();
/** A league's club book: clubId (of the name and every alias) → the display name. @param {Tournament} t */
export const clubBook = (t) => {
  let m = BOOKS.get(t);
  if (m) return m;
  m = new Map();
  for (const entry of t.league?.clubs ?? []) {
    const [name, ...aka] = entry.split('|');
    for (const n of [name, ...aka]) {
      const id = clubId(n);
      if (id && !m.has(id)) m.set(id, name);
    }
  }
  BOOKS.set(t, m);
  return m;
};

/** A side that is no club yet ("Winner of …", "TBD", a play-off slot). @param {string} s */
const placeholderSide = (s) => /\btbd\b|winner|runner|loser|play-?off|round of|\bgroup\b|qualifier/i.test(String(s));

/**
 * The game of a row inside a 'league' competition: { home, away, homeId, awayId } or null when the row belongs to
 * another competition. `row.teams` is [home, away]. The club list (when the entry has one) must name BOTH sides.
 * @param {Tournament} t @param {{ name?: string, tournament?: string | null, teams?: string[] | null, country?: string | null, kind?: string | null }} row
 */
export const leagueGame = (t, row) => {
  const L = t.league;
  const teams = row?.teams;
  if (!L || !Array.isArray(teams) || teams.length !== 2 || teams.some((x) => !x || placeholderSide(x))) return null;
  if (L.country && String(row.country ?? '').toUpperCase() !== L.country) return null;
  if (L.kinds && !L.kinds.includes(String(row.kind ?? ''))) return null;
  if (L.notGame?.test(String(row.name ?? ''))) return null;
  const tour = String(row.tournament ?? '');
  const ids = teams.map(clubId);
  if (ids[0] === ids[1] || !ids[0]) return null;
  if (L.clubs) {
    if (tour && !L.tournamentOk?.test(tour)) return null;
    const book = clubBook(t);
    const [home, away] = ids.map((id) => book.get(id));
    if (!home || !away) return null;
    return { home, away, homeId: clubId(home), awayId: clubId(away) };
  }
  if (!t.memberTest?.test(tour)) return null;
  return { home: teams[0], away: teams[1], homeId: ids[0], awayId: ids[1] };
};

/** Alias of isMember (the league branch lives there). */
export const isTournamentMember = (t, row) => isMember(t, row);

/** The Friday-to-Sunday that "this weekend" means on a day: the running one from Friday to Sunday, else the coming one.
 *  @param {string} iso @returns {{ from: string, until: string }} */
export const weekendOf = (iso) => {
  const dow = new Date(`${iso}T12:00:00Z`).getUTCDay(); // 0 Sunday
  const toFriday = dow === 0 ? -2 : dow === 6 ? -1 : dow === 5 ? 0 : 5 - dow;
  const from = dayAdd(iso, toFriday);
  return { from, until: dayAdd(from, 2) };
};

const LONG_DAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const LONG_MON = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/**
 * The sub-events of a series for structured data, each with its own name AND start: two entries with the same pair are one event to a
 * crawler (atp-finals-2026 listed "Session" on 2026-11-15 twice). The second and later entry of a pair gets its place in the list
 * ("Session 3 · 2"), so a hub that names sessions by number on several courts stays valid; builders that know a better label (a day or
 * night session, a court) give it, and this is only the net under them.
 * @template {{ name?: string, startDate?: string }} E @param {E[]} list @returns {E[]}
 */
export const uniqueSubEvents = (list) => {
  /** @type {Map<string, number>} */
  const seen = new Map();
  return list.map((e) => {
    const key = `${e.name}|${e.startDate}`;
    const n = (seen.get(key) ?? 0) + 1;
    seen.set(key, n);
    return n > 1 ? { ...e, name: `${e.name} · ${n}` } : e;
  });
};

/** "Sunday 30 May 2027" @param {string} iso */
export const dayLong = (iso) => {
  const d = new Date(`${iso}T12:00:00Z`);
  return `${LONG_DAY[d.getUTCDay()]} ${d.getUTCDate()} ${LONG_MON[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

// ---- readable dates (English; the page's chrome is translated, names and dates are data) ------------------
const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "Fri Dec 11" @param {string} iso */
export const dayLabel = (iso) => {
  const d = new Date(`${iso}T12:00:00Z`);
  return `${DAY[d.getUTCDay()]} ${MON[d.getUTCMonth()]} ${d.getUTCDate()}`;
};
